/*
  berry-recommendations — shared "you might also like" product-recommendation
  engine. ONE implementation of the fallback chain + variety cap, used by BOTH
  the cart drawer (assets/cart-drawer.js) and the product-page "You may also
  like" row (sections/berry-related-products.liquid) via the SAME
  fetchRelated → resolve call. The product row pre-renders its candidate cards
  hidden, then reveals only the ids this engine returns (fading them in once), so
  it never flickers and never re-implements the selection logic.

  The chain, in priority order, mirrors what a young store needs (Shopify's
  /recommendations/products.json?intent=related returns few/zero results on a
  small sandbox catalog, and one product type — e.g. the 8 lipstick shades —
  otherwise dominates the pool):
    1. API related products (data-driven; can be empty)
    2. top up (never replace) from products sharing a collection with the seed
    3. top up from a merchant-set priority collection (drawer only; optional)
    4. top up from the WHOLE-store pool
    5. always exclude the seed + anything the caller marks excluded (+ sold-out)
    6. compose with a same-type/collection variety cap so the row mixes
       categories instead of showing six near-identical products; only relaxed
       to reach the bare minimum when a single-category catalog leaves no choice.

  Pure data logic only — rendering is the caller's concern (the drawer draws its
  own compact cards; the product page reorders real product-card snippets), so
  the SAME selection runs behind two different card styles.

  Namespaced under window.BerryRecs. No dependencies, no auto-anything.
*/
(function () {
  'use strict';

  var TARGET = 6;     // show up to 6; 5–6 is the sweet spot
  var MIN = 3;        // fewer usable than this → caller hides the whole row
  var GROUP_CAP = 2;  // max cards from one type/collection group (relaxes to 3)

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  /* Variety key: product type first, else first collection handle — the lipstick
     shades share both, so either way they count as ONE group. */
  function groupKey(c) {
    return String(c.type || (c.cols && c.cols[0]) || 'misc').toLowerCase();
  }

  /* Map a raw API product to a card model, skipping excluded + sold-out ones. */
  function fromApi(list, exclude) {
    exclude = exclude || {};
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var p = list[i];
      if (!p || exclude[p.id]) { continue; }
      var v = null;
      if (p.variants) {
        for (var k = 0; k < p.variants.length; k++) {
          if (p.variants[k].available) { v = p.variants[k]; break; }
        }
      }
      if (!v) { continue; } // no available variant → treat as sold out, exclude
      out.push({
        id: p.id,
        title: p.title,
        url: p.url,
        image: p.featured_image || (p.images && p.images[0]) || '',
        price: p.price,
        variant_id: v.id,
        type: p.type || p.product_type || ''
      });
    }
    return out;
  }

  /* Compose the row from candidates already in priority order, enforcing the
     same-group cap so the row mixes categories. Two passes: cap 2, then relax to
     3 if the row is short. Only to reach the bare minimum (MIN) is the cap
     ignored — so a single-category catalog still gets a row, but never at the
     cost of variety when variety exists. */
  function composeRow(candidates, target) {
    if (!target) { target = TARGET; }
    var picked = [], chosen = {}, counts = {};
    var caps = [GROUP_CAP, GROUP_CAP + 1];
    for (var c = 0; c < caps.length && picked.length < target; c++) {
      for (var i = 0; i < candidates.length && picked.length < target; i++) {
        var cand = candidates[i];
        if (chosen[cand.id]) { continue; }
        var g = groupKey(cand);
        if ((counts[g] || 0) >= caps[c]) { continue; }
        chosen[cand.id] = true;
        counts[g] = (counts[g] || 0) + 1;
        picked.push(cand);
      }
    }
    for (var j = 0; j < candidates.length && picked.length < MIN; j++) {
      if (!chosen[candidates[j].id]) { chosen[candidates[j].id] = true; picked.push(candidates[j]); }
    }
    return picked;
  }

  /* Fetch the real Product Recommendations API (best effort — resolves to an
     empty product list on any failure so the pool chain still runs). */
  function fetchRelated(seedId) {
    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(seedId) + '&limit=10&intent=related';
    return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .catch(function () { return { products: [] }; });
  }

  /* Read the WHOLE-store fallback pool the cart drawer renders once per page
     ([data-cart-rec-fallback]); [{id,title,url,image,price,variant_id,type,cols}].
     Returns [] when the drawer/pool isn't on the page (callers that build their
     own pool — e.g. the product row from its rendered cards — pass it directly). */
  function getPool() {
    var el = document.querySelector('[data-cart-rec-fallback]');
    if (!el) { return []; }
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  /* Resolve the final ordered card list from API results + a fallback pool.
       opts.seedId       – the product the row is seeded on (excluded)
       opts.seedCols     – seed's collection handles (looked up in pool if omitted)
       opts.exclude      – map of product ids to drop (cart items, seed, …)
       opts.pool         – whole-store candidates [{id,type,cols,…}]
       opts.recCollection– optional merchant priority collection handle
       opts.apiProducts  – raw products from fetchRelated (may be empty)
       opts.target       – how many to aim for (default TARGET)
     Returns [] when fewer than MIN usable products exist (caller hides the row),
     otherwise a shuffled list of card models (each has at least an `id`). */
  function resolve(opts) {
    opts = opts || {};
    var pool = opts.pool || [];
    var exclude = opts.exclude || {};
    var seedCols = opts.seedCols;
    if (!seedCols) {
      seedCols = [];
      for (var s = 0; s < pool.length; s++) {
        if (pool[s] && pool[s].id === opts.seedId) { seedCols = pool[s].cols || []; break; }
      }
    }
    function sharesSeedCol(p) {
      if (!p.cols) { return false; }
      for (var a = 0; a < seedCols.length; a++) {
        if (p.cols.indexOf(seedCols[a]) !== -1) { return true; }
      }
      return false;
    }
    function poolWhere(test) {
      var out = [];
      for (var j = 0; j < pool.length; j++) {
        var p = pool[j];
        if (p && !exclude[p.id] && (!test || test(p))) { out.push(p); }
      }
      return shuffle(out);
    }
    var recCollection = opts.recCollection;
    var apiCards = shuffle(fromApi(opts.apiProducts, exclude));
    var sameCol = poolWhere(sharesSeedCol);
    var merchant = recCollection ? poolWhere(function (p) { return p.cols && p.cols.indexOf(recCollection) !== -1; }) : [];
    var everything = poolWhere(null);
    var candidates = apiCards.concat(sameCol, merchant, everything); // dupes resolved in composeRow
    var cards = composeRow(candidates, opts.target || TARGET);
    if (cards.length < MIN) { return []; }
    return shuffle(cards);
  }

  window.BerryRecs = {
    TARGET: TARGET,
    MIN: MIN,
    GROUP_CAP: GROUP_CAP,
    shuffle: shuffle,
    groupKey: groupKey,
    fromApi: fromApi,
    composeRow: composeRow,
    fetchRelated: fetchRelated,
    getPool: getPool,
    resolve: resolve
  };
})();
